<template>
  <div class="min-h-screen bg-slate-50">
    <div class="mx-auto flex w-full max-w-7xl flex-col gap-5 px-4 py-6">
      <header class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-teal-700">Admin</p>
          <h1 class="mt-1 text-3xl font-semibold text-slate-950">Companies</h1>
        </div>

        <div class="grid gap-2 sm:grid-cols-3">
          <div class="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p class="text-xs font-medium text-slate-500">Companies</p>
            <p class="mt-1 text-xl font-semibold text-slate-950">{{ companies.length }}</p>
          </div>
          <div class="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p class="text-xs font-medium text-slate-500">Active Jobs</p>
            <p class="mt-1 text-xl font-semibold text-slate-950">{{ totalActiveJobs }}</p>
          </div>
          <div class="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p class="text-xs font-medium text-slate-500">Matching Names</p>
            <p class="mt-1 text-xl font-semibold text-slate-950">{{ totalAliases }}</p>
          </div>
        </div>
      </header>

      <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div class="grid gap-3 lg:grid-cols-[1fr_190px_190px_170px]">
          <label class="relative block">
            <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              v-model="query"
              type="search"
              class="h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
              placeholder="Search company, alias, contact, website, address"
            />
          </label>

          <select v-model="industryFilter" class="field">
            <option value="">All industries</option>
            <option v-for="industry in industryOptions" :key="industry" :value="industry">{{ industry }}</option>
          </select>

          <select v-model="activityFilter" class="field">
            <option value="all">All activity</option>
            <option value="active_jobs">Has active jobs</option>
            <option value="aliases">Has matching names</option>
            <option value="needs_aliases">No matching names</option>
          </select>

          <select v-model="sortBy" class="field">
            <option value="name">Name A-Z</option>
            <option value="recent">Newest</option>
            <option value="jobs">Active jobs</option>
            <option value="credits">Credits</option>
          </select>
        </div>
      </section>

      <main class="grid min-h-[680px] gap-5 lg:grid-cols-[390px_1fr]">
        <aside class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <p class="text-sm font-semibold text-slate-900">{{ filteredCompanies.length }} results</p>
            <button
              v-if="query || industryFilter || activityFilter !== 'all'"
              type="button"
              class="text-xs font-semibold text-teal-700 hover:text-teal-900"
              @click="clearFilters"
            >
              Clear
            </button>
          </div>

          <div class="max-h-[760px] overflow-y-auto">
            <button
              v-for="company in filteredCompanies"
              :key="company.id"
              type="button"
              class="flex w-full gap-3 border-b border-slate-100 px-4 py-4 text-left transition hover:bg-slate-50"
              :class="selectedCompany && selectedCompany.id === company.id ? 'bg-teal-50/70' : 'bg-white'"
              @click="selectCompany(company)"
            >
              <div class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100 text-sm font-bold text-slate-600">
                <img v-if="company.logo" :src="company.logo" :alt="company.name" class="h-full w-full object-contain" />
                <span v-else>{{ companyInitial(company.name) }}</span>
              </div>

              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold text-slate-950">{{ company.name || 'Unnamed company' }}</p>
                    <p class="mt-0.5 truncate text-xs text-slate-500">{{ company.industry_name || company.industry || 'Industry not set' }}</p>
                  </div>
                  <span class="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">{{ company.jobsActive || 0 }} active</span>
                </div>

                <div class="mt-3 flex flex-wrap gap-1.5">
                  <span
                    v-for="alias in company.company_aliases?.slice(0, 3) || []"
                    :key="alias"
                    class="rounded-full bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800"
                  >
                    {{ alias }}
                  </span>
                  <span v-if="(company.company_aliases || []).length > 3" class="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600">
                    +{{ company.company_aliases.length - 3 }}
                  </span>
                </div>
              </div>
            </button>

            <div v-if="!filteredCompanies.length" class="px-4 py-12 text-center">
              <Building2 class="mx-auto h-8 w-8 text-slate-300" />
              <p class="mt-3 text-sm font-semibold text-slate-900">No matching companies</p>
            </div>
          </div>
        </aside>

        <section v-if="form" class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div class="border-b border-slate-200 px-5 py-4">
            <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div class="flex min-w-0 items-center gap-3">
                <div class="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100 text-lg font-bold text-slate-600">
                  <img v-if="form.logo" :src="form.logo" :alt="form.name" class="h-full w-full object-contain" />
                  <span v-else>{{ companyInitial(form.name) }}</span>
                </div>
                <div class="min-w-0">
                  <h2 class="truncate text-xl font-semibold text-slate-950">{{ form.name || 'Unnamed company' }}</h2>
                  <p class="mt-1 text-sm text-slate-500">{{ form.website || form.office_address || 'Company details' }}</p>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <button type="button" class="icon-button" title="Add credits" @click="openCreditModal">
                  <Plus class="h-4 w-4" />
                  <span>Add credits</span>
                </button>
                <button type="button" class="icon-button" title="Discard edits" :disabled="!hasChanges" @click="resetForm">
                  <RotateCcw class="h-4 w-4" />
                  <span>Reset</span>
                </button>
                <button type="button" class="primary-button" :disabled="isSaving || !hasChanges" @click="saveCompany(false)">
                  <Save class="h-4 w-4" />
                  <span>{{ isSaving ? 'Saving' : 'Save' }}</span>
                </button>
              </div>
            </div>

            <nav class="mt-5 flex flex-wrap gap-2">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                class="rounded-lg px-3 py-2 text-sm font-semibold transition"
                :class="activeTab === tab.id ? 'bg-slate-950 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-950'"
                @click="activeTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </nav>
          </div>

          <div class="max-h-[720px] overflow-y-auto p-5">
            <div v-if="formErrors.length" class="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
              <p class="text-sm font-semibold text-rose-800">Please fix these fields before saving.</p>
              <ul class="mt-2 list-disc space-y-1 pl-5 text-sm text-rose-700">
                <li v-for="error in formErrors" :key="error">{{ error }}</li>
              </ul>
            </div>

            <div v-if="activeTab === 'profile'" class="grid gap-4 md:grid-cols-2">
              <label class="block">
                <span class="label">Company name</span>
                <input v-model="form.name" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Slug</span>
                <input v-model="form.slug" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Website</span>
                <input v-model="form.website" type="url" class="field" />
              </label>
              <label class="block">
                <span class="label">Contact email</span>
                <input v-model="form.contact_email" type="email" class="field" />
              </label>
              <label class="block md:col-span-2">
                <span class="label">Logo URL</span>
                <input v-model="form.logo" type="url" class="field" />
              </label>
              <label class="block md:col-span-2">
                <span class="label">Office address</span>
                <input v-model="form.office_address" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Industry</span>
                <input v-model="form.industry" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Employee size</span>
                <input v-model="form.employee_size" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Headcount Growth (Last 12 Months) (%)</span>
                <input v-model="form.headcount_growth_12_months" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Average Employee Tenure (years)</span>
                <input v-model="form.average_employee_tenure" type="text" class="field" />
              </label>
              <label class="block md:col-span-2">
                <span class="label">Summary</span>
                <textarea v-model="form.summary" rows="6" class="field min-h-[150px]" />
              </label>
            </div>

            <div v-else-if="activeTab === 'matching'" class="flex flex-col gap-5">
              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label class="block flex-1">
                    <span class="label">Additional company name</span>
                    <input
                      v-model="aliasInput"
                      type="text"
                      class="field"
                      placeholder="BHP"
                      @keydown.enter.prevent="addAlias"
                    />
                  </label>
                  <button type="button" class="primary-button h-11" @click="addAlias">
                    <Plus class="h-4 w-4" />
                    <span>Add</span>
                  </button>
                </div>

                <div class="mt-4 flex flex-wrap gap-2">
                  <span
                    v-for="alias in form.company_aliases"
                    :key="alias"
                    class="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-900"
                  >
                    {{ alias }}
                    <button type="button" class="text-amber-700 hover:text-amber-950" :title="`Remove ${alias}`" @click="removeAlias(alias)">
                      <X class="h-3.5 w-3.5" />
                    </button>
                  </span>
                  <span v-if="!form.company_aliases.length" class="text-sm text-slate-500">No additional names</span>
                </div>

                <div v-if="localConflicts.length" class="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <div class="flex gap-2">
                    <AlertTriangle class="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                    <div>
                      <p class="text-sm font-semibold text-amber-900">Possible matching clash</p>
                      <p v-for="conflict in localConflicts" :key="`${conflict.value}-${conflict.company_id}-${conflict.source}`" class="mt-1 text-sm text-amber-800">
                        {{ conflict.value }} matches {{ conflict.company_name }}.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <div class="grid gap-4 md:grid-cols-2">
                <section class="rounded-lg border border-slate-200 p-4">
                  <div class="flex items-center justify-between gap-3">
                    <h3 class="text-sm font-semibold text-slate-900">Commodities</h3>
                    <button type="button" class="small-button" @click="addListItem('commodities')">
                      <Plus class="h-3.5 w-3.5" />
                      <span>Add</span>
                    </button>
                  </div>
                  <div class="mt-3 space-y-2">
                    <div v-for="(commodity, index) in form.commodities" :key="`commodity-${index}`" class="flex gap-2">
                      <input v-model="form.commodities[index]" type="text" class="field" placeholder="Gold" />
                      <button type="button" class="icon-only-button" title="Remove commodity" @click="removeListItem('commodities', index)">
                        <X class="h-4 w-4" />
                      </button>
                    </div>
                    <p v-if="!form.commodities.length" class="text-sm text-slate-500">No commodities listed</p>
                  </div>
                </section>

                <section class="rounded-lg border border-slate-200 p-4">
                  <div class="flex items-center justify-between gap-3">
                    <h3 class="text-sm font-semibold text-slate-900">Software used</h3>
                    <button type="button" class="small-button" @click="addListItem('software_used')">
                      <Plus class="h-3.5 w-3.5" />
                      <span>Add</span>
                    </button>
                  </div>
                  <div class="mt-3 space-y-2">
                    <div v-for="(software, index) in form.software_used" :key="`software-${index}`" class="flex gap-2">
                      <input v-model="form.software_used[index]" type="text" class="field" placeholder="Vulcan" />
                      <button type="button" class="icon-only-button" title="Remove software" @click="removeListItem('software_used', index)">
                        <X class="h-4 w-4" />
                      </button>
                    </div>
                    <p v-if="!form.software_used.length" class="text-sm text-slate-500">No software listed</p>
                  </div>
                </section>
              </div>
            </div>

            <div v-else-if="activeTab === 'market'" class="grid gap-4 md:grid-cols-2">
              <label class="block">
                <span class="label">Ticker</span>
                <input v-model="form.ticker" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Market cap</span>
                <input v-model="form.market_cap" type="text" class="field" />
              </label>
              <label class="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
                <input v-model="form.is_private_company" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-600" />
                <span class="text-sm font-medium text-slate-700">Private company</span>
              </label>
              <label class="block">
                <span class="label">Cash in bank</span>
                <input v-model="form.cash_in_bank" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Cash updated at</span>
                <input v-model="form.cash_in_bank_updated_at" type="date" class="field" />
              </label>
              <label class="block">
                <span class="label">Cash runway</span>
                <input v-model="form.cash_runway" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Field roster</span>
                <input v-model="form.field_roster" type="text" class="field" />
              </label>
              <label class="block">
                <span class="label">Typical hours</span>
                <input v-model="form.typical_hours" type="text" class="field" />
              </label>
              <label class="block md:col-span-2">
                <span class="label">Facilities</span>
                <textarea v-model="form.facilities" rows="4" class="field" />
              </label>
            </div>

            <div v-else class="space-y-5">
              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Key projects</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('key_projects')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add project</span>
                  </button>
                </div>
                <div class="mt-4 space-y-4">
                  <article v-for="(project, index) in form.key_projects" :key="`project-${index}`" class="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div class="grid gap-3 md:grid-cols-2">
                      <label class="block">
                        <span class="label">Project name</span>
                        <input v-model="project.name" type="text" class="field" placeholder="Olympic Dam" />
                      </label>
                      <label class="block">
                        <span class="label">Stage</span>
                        <input v-model="project.stage" type="text" class="field" placeholder="Production" />
                      </label>
                      <label class="block md:col-span-2">
                        <span class="label">Description</span>
                        <textarea v-model="project.description" rows="3" class="field" />
                      </label>
                      <label class="block">
                        <span class="label">Location label</span>
                        <input v-model="project.location_label" type="text" class="field" placeholder="Roxby Downs, SA" />
                      </label>
                      <label class="block">
                        <span class="label">Commodities</span>
                        <input :value="listToCsv(project.commodities)" type="text" class="field" placeholder="Copper, Uranium" @input="project.commodities = csvToList($event.target.value)" />
                      </label>
                      <label class="block">
                        <span class="label">Latitude</span>
                        <input v-model="project.location_lat" type="text" class="field" />
                      </label>
                      <label class="block">
                        <span class="label">Longitude</span>
                        <input v-model="project.location_lng" type="text" class="field" />
                      </label>
                    </div>
                    <div class="mt-4 flex justify-end">
                      <button type="button" class="danger-button" @click="removeStructuredRow('key_projects', index)">Remove project</button>
                    </div>
                  </article>
                  <p v-if="!form.key_projects.length" class="text-sm text-slate-500">No projects listed</p>
                </div>
              </section>

              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Key people</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('key_people')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add person</span>
                  </button>
                </div>
                <div class="mt-4 space-y-3">
                  <div v-for="(person, index) in form.key_people" :key="`person-${index}`" class="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-[1fr_1fr_1fr_150px_auto]">
                    <input v-model="person.first_name" type="text" class="field" placeholder="First name" />
                    <input v-model="person.last_name" type="text" class="field" placeholder="Last name" />
                    <input v-model="person.role" type="text" class="field" placeholder="Role" />
                    <input v-model="person.role_start_date" type="date" class="field" />
                    <button type="button" class="icon-only-button" title="Remove person" @click="removeStructuredRow('key_people', index)">
                      <X class="h-4 w-4" />
                    </button>
                  </div>
                  <p v-if="!form.key_people.length" class="text-sm text-slate-500">No key people listed</p>
                </div>
              </section>

              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Job links</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('job_links')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add link</span>
                  </button>
                </div>
                <div class="mt-4 space-y-3">
                  <div v-for="(link, index) in form.job_links" :key="`job-link-${index}`" class="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-[140px_1fr_1fr_auto]">
                    <select v-model="link.type" class="field">
                      <option value="linkedin">LinkedIn</option>
                      <option value="seek">SEEK</option>
                      <option value="other">Other</option>
                    </select>
                    <input v-model="link.title" type="text" class="field" placeholder="Careers page" />
                    <input v-model="link.url" type="url" class="field" placeholder="https://..." />
                    <button type="button" class="icon-only-button" title="Remove link" @click="removeStructuredRow('job_links', index)">
                      <X class="h-4 w-4" />
                    </button>
                    <textarea v-model="link.description" rows="2" class="field md:col-span-4" placeholder="Description" />
                  </div>
                  <p v-if="!form.job_links.length" class="text-sm text-slate-500">No job links listed</p>
                </div>
              </section>

              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Social links</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('social_links')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add social</span>
                  </button>
                </div>
                <div class="mt-4 space-y-3">
                  <div v-for="(link, index) in form.social_links" :key="`social-${index}`" class="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-[180px_1fr_auto]">
                    <select v-model="link.type" class="field">
                      <option value="linkedin">LinkedIn</option>
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="x">X</option>
                      <option value="youtube">YouTube</option>
                    </select>
                    <input v-model="link.url" type="url" class="field" placeholder="https://..." />
                    <button type="button" class="icon-only-button" title="Remove social link" @click="removeStructuredRow('social_links', index)">
                      <X class="h-4 w-4" />
                    </button>
                  </div>
                  <p v-if="!form.social_links.length" class="text-sm text-slate-500">No social links listed</p>
                </div>
              </section>

              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Recent key hires</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('recent_key_hires')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add hire</span>
                  </button>
                </div>
                <div class="mt-4 space-y-3">
                  <div v-for="(hire, index) in form.recent_key_hires" :key="`hire-${index}`" class="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-2">
                    <input v-model="hire.name" type="text" class="field" placeholder="Name" />
                    <input v-model="hire.role" type="text" class="field" placeholder="Role" />
                    <input v-model="hire.date" type="date" class="field" />
                    <input v-model="hire.url" type="url" class="field" placeholder="Source URL" />
                    <input v-model="hire.background" type="text" class="field" placeholder="Background" />
                    <input v-model="hire.summary" type="text" class="field" placeholder="Summary" />
                    <div class="md:col-span-2 flex justify-end">
                      <button type="button" class="danger-button" @click="removeStructuredRow('recent_key_hires', index)">Remove hire</button>
                    </div>
                  </div>
                  <p v-if="!form.recent_key_hires.length" class="text-sm text-slate-500">No recent hires listed</p>
                </div>
              </section>

              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Recent capital raisings</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('recent_capital_raisings')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add raising</span>
                  </button>
                </div>
                <div class="mt-4 space-y-3">
                  <div v-for="(raising, index) in form.recent_capital_raisings" :key="`raising-${index}`" class="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-2">
                    <input v-model="raising.date" type="date" class="field" />
                    <input v-model="raising.amount" type="text" class="field" placeholder="$10m" />
                    <input v-model="raising.label" type="text" class="field" placeholder="Placement" />
                    <input v-model="raising.url" type="url" class="field" placeholder="Source URL" />
                    <textarea v-model="raising.summary" rows="2" class="field md:col-span-2" placeholder="Summary" />
                    <div class="md:col-span-2 flex justify-end">
                      <button type="button" class="danger-button" @click="removeStructuredRow('recent_capital_raisings', index)">Remove raising</button>
                    </div>
                  </div>
                  <p v-if="!form.recent_capital_raisings.length" class="text-sm text-slate-500">No capital raisings listed</p>
                </div>
              </section>

              <section class="rounded-lg border border-slate-200 p-4">
                <div class="flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold text-slate-900">Benefits</h3>
                  <button type="button" class="small-button" @click="addStructuredRow('benefits')">
                    <Plus class="h-3.5 w-3.5" />
                    <span>Add benefit</span>
                  </button>
                </div>
                <div class="mt-4 space-y-3">
                  <div v-for="(benefit, index) in form.benefits" :key="`benefit-${index}`" class="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 md:grid-cols-2">
                    <input v-model="benefit.heading" type="text" class="field" placeholder="Heading" />
                    <input v-model="benefit.source" type="url" class="field" placeholder="Source URL" />
                    <input v-model="benefit.added_at" type="date" class="field" />
                    <textarea v-model="benefit.description" rows="2" class="field md:col-span-2" placeholder="Description" />
                    <div class="md:col-span-2 flex justify-end">
                      <button type="button" class="danger-button" @click="removeStructuredRow('benefits', index)">Remove benefit</button>
                    </div>
                  </div>
                  <p v-if="!form.benefits.length" class="text-sm text-slate-500">No benefits listed</p>
                </div>
              </section>
            </div>
          </div>
        </section>

        <section v-else class="flex items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white">
          <div class="px-6 py-12 text-center">
            <Building2 class="mx-auto h-10 w-10 text-slate-300" />
            <p class="mt-3 text-sm font-semibold text-slate-900">Select a company</p>
          </div>
        </section>
      </main>
    </div>

    <div v-if="showConflictModal" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
      <section class="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
        <div class="flex items-start gap-3">
          <div class="rounded-lg bg-amber-100 p-2 text-amber-700">
            <AlertTriangle class="h-5 w-5" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-slate-950">Matching clash warning</h3>
            <div class="mt-3 space-y-2">
              <p v-for="conflict in pendingConflicts" :key="`${conflict.value}-${conflict.company_id}-${conflict.source}`" class="text-sm text-slate-600">
                <span class="font-semibold text-slate-900">{{ conflict.value }}</span>
                matches {{ conflict.company_name }}{{ conflict.matched_alias ? ` via ${conflict.matched_alias}` : '' }}.
              </p>
            </div>
          </div>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <button type="button" class="icon-button" @click="showConflictModal = false">Cancel</button>
          <button type="button" class="primary-button bg-amber-700 hover:bg-amber-800" @click="confirmConflictSave">
            <Save class="h-4 w-4" />
            <span>Save anyway</span>
          </button>
        </div>
      </section>
    </div>

    <div v-if="showCreditModal" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
      <section class="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h3 class="text-lg font-semibold text-slate-950">Add Credits</h3>
        <p class="mt-1 text-sm text-slate-500">{{ selectedCompany?.name }}</p>

        <div class="mt-5 space-y-4">
          <label class="block">
            <span class="label">Number of credits</span>
            <input v-model="creditAmount" min="1" max="100" type="number" class="field" />
          </label>
          <label class="block">
            <span class="label">Reason</span>
            <input v-model="creditReason" type="text" class="field" placeholder="Admin allocation" />
          </label>
          <label class="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3">
            <input v-model="confirmCreditAddition" type="checkbox" class="h-4 w-4 rounded border-slate-300 text-teal-700 focus:ring-teal-600" />
            <span class="text-sm text-slate-700">Confirm {{ creditAmount || 0 }} credits</span>
          </label>
        </div>

        <div class="mt-5 flex justify-end gap-2">
          <button type="button" class="icon-button" @click="closeCreditModal">Cancel</button>
          <button type="button" class="primary-button" :disabled="!isValidCreditForm" @click="confirmAddCredits">
            <Plus class="h-4 w-4" />
            <span>Add</span>
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  AlertTriangle,
  Building2,
  Plus,
  RotateCcw,
  Save,
  Search,
  X,
} from 'lucide-vue-next'
import adminService from '@/services/marketplace/admin.service'

const JSON_FIELDS = [
  'commodities',
  'software_used',
  'key_people',
  'key_projects',
  'recent_key_hires',
  'job_links',
  'social_links',
  'recent_capital_raisings',
  'benefits',
]

export default {
  name: 'AdminCompanyList',
  inject: ['toggleLoading'],
  components: {
    AlertTriangle,
    Building2,
    Plus,
    RotateCcw,
    Save,
    Search,
    X,
  },
  data() {
    return {
      companies: [],
      selectedCompanyId: null,
      query: '',
      industryFilter: '',
      activityFilter: 'all',
      sortBy: 'name',
      activeTab: 'profile',
      form: null,
      originalSnapshot: '',
      aliasInput: '',
      formErrors: [],
      isSaving: false,
      showConflictModal: false,
      pendingConflicts: [],
      showCreditModal: false,
      creditAmount: 1,
      creditReason: '',
      confirmCreditAddition: false,
      tabs: [
        { id: 'profile', label: 'Profile' },
        { id: 'matching', label: 'Matching' },
        { id: 'market', label: 'Market' },
        { id: 'structured', label: 'Structured' },
      ],
    }
  },
  computed: {
    selectedCompany() {
      return this.companies.find((company) => String(company.id) === String(this.selectedCompanyId)) || null
    },
    totalActiveJobs() {
      return this.companies.reduce((total, company) => total + Number(company.jobsActive || 0), 0)
    },
    totalAliases() {
      return this.companies.reduce((total, company) => total + (company.company_aliases || []).length, 0)
    },
    industryOptions() {
      return [...new Set(this.companies.map((company) => company.industry_name || company.industry).filter(Boolean))].sort()
    },
    filteredCompanies() {
      const needle = this.normalizeSearch(this.query)
      const companies = this.companies.filter((company) => {
        const aliases = (company.company_aliases || []).join(' ')
        const haystack = this.normalizeSearch([
          company.name,
          aliases,
          company.email,
          company.website,
          company.office_address,
          company.industry_name,
          company.summary,
        ].filter(Boolean).join(' '))

        if (needle && !haystack.includes(needle)) return false
        if (this.industryFilter && (company.industry_name || company.industry) !== this.industryFilter) return false
        if (this.activityFilter === 'active_jobs' && Number(company.jobsActive || 0) <= 0) return false
        if (this.activityFilter === 'aliases' && !(company.company_aliases || []).length) return false
        if (this.activityFilter === 'needs_aliases' && (company.company_aliases || []).length) return false
        return true
      })

      return companies.sort((a, b) => {
        if (this.sortBy === 'name') return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
        if (this.sortBy === 'jobs') return Number(b.jobsActive || 0) - Number(a.jobsActive || 0)
        if (this.sortBy === 'credits') return Number(b.credits || 0) - Number(a.credits || 0)
        return String(b.creation_date || '').localeCompare(String(a.creation_date || ''))
      })
    },
    hasChanges() {
      if (!this.form) return false
      try {
        return this.serializeCurrentForm() !== this.originalSnapshot
      } catch {
        return true
      }
    },
    localConflicts() {
      if (!this.form) return []
      return this.findLocalConflicts([this.form.name, ...(this.form.company_aliases || [])])
    },
    isValidCreditForm() {
      return this.confirmCreditAddition && Number(this.creditAmount) > 0 && Number(this.creditAmount) <= 100
    },
  },
  methods: {
    fetchCompanies() {
      this.toggleLoading(true)
      adminService.getCompanies()
        .then((response) => {
          this.companies = (response.data || []).map(this.normalizeCompanyRecord)
          if (!this.selectedCompanyId && this.companies.length) {
            this.selectCompany(this.companies[0])
          } else if (this.selectedCompanyId) {
            const selected = this.selectedCompany
            if (selected) this.prepareForm(selected)
          }
        })
        .catch(() => {
          this.$router.push('/dashboard/auth')
        })
        .finally(() => {
          this.toggleLoading(false)
        })
    },
    normalizeCompanyRecord(company) {
      const normalized = { ...company }
      normalized.company_aliases = Array.isArray(company.company_aliases) ? company.company_aliases : []
      JSON_FIELDS.forEach((field) => {
        normalized[field] = this.parseJsonValue(company[field])
      })
      return normalized
    },
    parseJsonValue(value) {
      if (Array.isArray(value)) return value
      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }
      return []
    },
    selectCompany(company) {
      this.selectedCompanyId = company.id
      this.activeTab = 'profile'
      this.prepareForm(company)
    },
    prepareForm(company) {
      this.form = {
        id: company.id,
        name: company.name || '',
        slug: company.slug || '',
        logo: company.logo || '',
        website: company.website || '',
        contact_email: company.contact_email || company.email || '',
        office_address: company.office_address || '',
        employee_size: company.employee_size || '',
        headcount_growth_12_months: company.headcount_growth_12_months || '',
        average_employee_tenure: company.average_employee_tenure || '',
        industry: company.industry || '',
        summary: company.summary || '',
        company_aliases: [...(company.company_aliases || [])],
        ticker: company.ticker || '',
        market_cap: company.market_cap || '',
        is_private_company: Boolean(Number(company.is_private_company || 0)),
        cash_in_bank: company.cash_in_bank || '',
        cash_in_bank_updated_at: company.cash_in_bank_updated_at || '',
        cash_runway: company.cash_runway || '',
        field_roster: company.field_roster || '',
        typical_hours: company.typical_hours || '',
        facilities: company.facilities || '',
        commodities: this.normalizeStringList(company.commodities),
        software_used: this.normalizeStringList(company.software_used),
        key_people: this.normalizeKeyPeople(company.key_people),
        key_projects: this.normalizeProjects(company.key_projects),
        recent_key_hires: this.normalizeRecentKeyHires(company.recent_key_hires),
        job_links: this.normalizeJobLinks(company.job_links),
        social_links: this.normalizeSocialLinks(company.social_links),
        recent_capital_raisings: this.normalizeRecentCapitalRaisings(company.recent_capital_raisings),
        benefits: this.normalizeBenefits(company.benefits),
      }
      this.formErrors = []
      this.originalSnapshot = this.serializeCurrentForm()
    },
    serializeCurrentForm() {
      return JSON.stringify(this.buildPayload(false))
    },
    buildPayload(allowConflicts) {
      const payload = { ...(this.form || {}) }
      delete payload.id
      payload.commodities = this.cleanStringList(payload.commodities)
      payload.software_used = this.cleanStringList(payload.software_used)
      payload.key_people = this.cleanKeyPeople(payload.key_people)
      payload.key_projects = this.cleanProjects(payload.key_projects)
      payload.recent_key_hires = this.cleanRecentKeyHires(payload.recent_key_hires)
      payload.job_links = this.cleanJobLinks(payload.job_links)
      payload.social_links = this.cleanSocialLinks(payload.social_links)
      payload.recent_capital_raisings = this.cleanRecentCapitalRaisings(payload.recent_capital_raisings)
      payload.benefits = this.cleanBenefits(payload.benefits)
      payload.allow_conflicts = allowConflicts
      return payload
    },
    saveCompany(allowConflicts) {
      if (!this.form || this.isSaving) return
      this.formErrors = this.validateForm()
      if (this.formErrors.length) return

      if (!allowConflicts && this.localConflicts.length) {
        this.pendingConflicts = this.localConflicts
        this.showConflictModal = true
        return
      }

      let payload
      try {
        payload = this.buildPayload(allowConflicts)
      } catch (error) {
        this.formErrors = [error.message]
        return
      }

      this.isSaving = true
      adminService.updateCompany(this.form.id, payload)
        .then((response) => {
          if (response.data?.requires_confirmation) {
            this.pendingConflicts = response.data.conflicts || []
            this.showConflictModal = true
            return
          }
          this.applySavedCompany(response.data?.company)
          this.$root.notify('Company saved', 'success')
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            this.pendingConflicts = error.response.data?.conflicts || []
            this.showConflictModal = true
            return
          }
          this.$root.notify(error.response?.data?.error || 'Failed to save company', 'error')
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    confirmConflictSave() {
      this.showConflictModal = false
      this.saveCompany(true)
    },
    applySavedCompany(company) {
      if (!company) return
      const normalized = this.normalizeCompanyRecord(company)
      const index = this.companies.findIndex((item) => String(item.id) === String(normalized.id))
      if (index !== -1) this.companies.splice(index, 1, normalized)
      this.prepareForm(normalized)
    },
    resetForm() {
      if (this.selectedCompany) this.prepareForm(this.selectedCompany)
    },
    addListItem(field) {
      if (!Array.isArray(this.form[field])) this.form[field] = []
      this.form[field].push('')
    },
    removeListItem(field, index) {
      this.form[field].splice(index, 1)
    },
    addStructuredRow(field) {
      if (!Array.isArray(this.form[field])) this.form[field] = []
      this.form[field].push(this.emptyStructuredRow(field))
    },
    removeStructuredRow(field, index) {
      this.form[field].splice(index, 1)
    },
    emptyStructuredRow(field) {
      const rows = {
        key_projects: {
          name: '',
          stage: '',
          description: '',
          location_label: '',
          location_lat: '',
          location_lng: '',
          commodities: [],
          activity_feed: [],
        },
        key_people: { first_name: '', last_name: '', role: '', role_start_date: '' },
        recent_key_hires: { name: '', role: '', date: '', background: '', summary: '', url: '' },
        job_links: { type: 'other', title: '', description: '', url: '' },
        social_links: { type: 'linkedin', url: '' },
        recent_capital_raisings: { date: '', amount: '', label: '', summary: '', url: '' },
        benefits: { heading: '', description: '', source: '', added_at: '' },
      }
      return { ...rows[field] }
    },
    normalizeStringList(value) {
      return this.parseJsonValue(value).map((item) => String(item || '').trim()).filter(Boolean)
    },
    cleanStringList(value) {
      return (Array.isArray(value) ? value : []).map((item) => String(item || '').trim()).filter(Boolean)
    },
    normalizeKeyPeople(value) {
      return this.parseJsonValue(value).map((person) => ({
        first_name: String(person?.first_name || person?.firstName || '').trim(),
        last_name: String(person?.last_name || person?.lastName || '').trim(),
        role: String(person?.role || person?.title || '').trim(),
        role_start_date: String(person?.role_start_date || person?.roleStartDate || '').trim(),
      })).filter((person) => person.first_name || person.last_name || person.role || person.role_start_date)
    },
    cleanKeyPeople(value) {
      return (Array.isArray(value) ? value : []).map((person) => ({
        first_name: String(person?.first_name || '').trim(),
        last_name: String(person?.last_name || '').trim(),
        role: String(person?.role || '').trim(),
        role_start_date: String(person?.role_start_date || '').trim(),
      })).filter((person) => person.first_name || person.last_name || person.role || person.role_start_date)
    },
    normalizeProjects(value) {
      return this.parseJsonValue(value).map((project) => ({
        name: String(project?.name || '').trim(),
        stage: String(project?.stage || '').trim(),
        description: String(project?.description || '').trim(),
        location_label: String(project?.location_label || project?.locationLabel || '').trim(),
        location_lat: project?.location_lat ?? project?.locationLat ?? '',
        location_lng: project?.location_lng ?? project?.locationLng ?? '',
        commodities: Array.isArray(project?.commodities) ? project.commodities.map((item) => String(item || '').trim()).filter(Boolean) : [],
        activity_feed: Array.isArray(project?.activity_feed) ? project.activity_feed : [],
      })).filter((project) => project.name || project.stage || project.description || project.location_label || project.commodities.length)
    },
    cleanProjects(value) {
      return (Array.isArray(value) ? value : []).map((project) => ({
        name: String(project?.name || '').trim(),
        stage: String(project?.stage || '').trim(),
        description: String(project?.description || '').trim(),
        location_label: String(project?.location_label || '').trim(),
        location_lat: String(project?.location_lat || '').trim(),
        location_lng: String(project?.location_lng || '').trim(),
        commodities: this.cleanStringList(project?.commodities),
        activity_feed: Array.isArray(project?.activity_feed) ? project.activity_feed : [],
      })).filter((project) => project.name || project.stage || project.description || project.location_label || project.commodities.length)
    },
    normalizeRecentKeyHires(value) {
      return this.parseJsonValue(value).map((hire) => ({
        name: String(hire?.name || '').trim(),
        role: String(hire?.role || '').trim(),
        date: String(hire?.date || '').trim(),
        background: String(hire?.background || '').trim(),
        summary: String(hire?.summary || '').trim(),
        url: String(hire?.url || '').trim(),
      })).filter((hire) => hire.name || hire.role || hire.date || hire.background || hire.summary || hire.url)
    },
    cleanRecentKeyHires(value) {
      return (Array.isArray(value) ? value : []).map((hire) => ({
        name: String(hire?.name || '').trim(),
        role: String(hire?.role || '').trim(),
        date: String(hire?.date || '').trim(),
        background: String(hire?.background || '').trim(),
        summary: String(hire?.summary || '').trim(),
        url: String(hire?.url || '').trim(),
      })).filter((hire) => hire.name || hire.role || hire.date || hire.background || hire.summary || hire.url)
    },
    normalizeJobLinks(value) {
      return this.parseJsonValue(value).map((link) => ({
        type: ['linkedin', 'seek', 'other'].includes(String(link?.type || '').toLowerCase()) ? String(link.type).toLowerCase() : 'other',
        title: String(link?.title || '').trim(),
        description: String(link?.description || '').trim(),
        url: String(link?.url || '').trim(),
      })).filter((link) => link.title || link.description || link.url)
    },
    cleanJobLinks(value) {
      return (Array.isArray(value) ? value : []).map((link) => ({
        type: ['linkedin', 'seek', 'other'].includes(String(link?.type || '').toLowerCase()) ? String(link.type).toLowerCase() : 'other',
        title: String(link?.title || '').trim(),
        description: String(link?.description || '').trim(),
        url: String(link?.url || '').trim(),
      })).filter((link) => link.title || link.description || link.url)
    },
    normalizeSocialLinks(value) {
      return this.parseJsonValue(value).map((link) => ({
        type: ['linkedin', 'facebook', 'instagram', 'x', 'youtube'].includes(String(link?.type || '').toLowerCase()) ? String(link.type).toLowerCase() : 'linkedin',
        url: String(link?.url || '').trim(),
      })).filter((link) => link.url)
    },
    cleanSocialLinks(value) {
      return (Array.isArray(value) ? value : []).map((link) => ({
        type: ['linkedin', 'facebook', 'instagram', 'x', 'youtube'].includes(String(link?.type || '').toLowerCase()) ? String(link.type).toLowerCase() : 'linkedin',
        url: String(link?.url || '').trim(),
      })).filter((link) => link.url)
    },
    normalizeRecentCapitalRaisings(value) {
      return this.parseJsonValue(value).map((raising) => ({
        date: String(raising?.date || '').trim(),
        amount: String(raising?.amount || '').trim(),
        label: String(raising?.label || '').trim(),
        summary: String(raising?.summary || '').trim(),
        url: String(raising?.url || '').trim(),
      })).filter((raising) => raising.date || raising.amount || raising.label || raising.summary || raising.url)
    },
    cleanRecentCapitalRaisings(value) {
      return (Array.isArray(value) ? value : []).map((raising) => ({
        date: String(raising?.date || '').trim(),
        amount: String(raising?.amount || '').trim(),
        label: String(raising?.label || '').trim(),
        summary: String(raising?.summary || '').trim(),
        url: String(raising?.url || '').trim(),
      })).filter((raising) => raising.date || raising.amount || raising.label || raising.summary || raising.url)
    },
    normalizeBenefits(value) {
      return this.parseJsonValue(value).map((benefit) => ({
        heading: String(benefit?.heading || '').trim(),
        description: String(benefit?.description || '').trim(),
        source: String(benefit?.source || '').trim(),
        added_at: String(benefit?.added_at || benefit?.addedAt || '').trim(),
      })).filter((benefit) => benefit.heading || benefit.description || benefit.source || benefit.added_at)
    },
    cleanBenefits(value) {
      return (Array.isArray(value) ? value : []).map((benefit) => ({
        heading: String(benefit?.heading || '').trim(),
        description: String(benefit?.description || '').trim(),
        source: String(benefit?.source || '').trim(),
        added_at: String(benefit?.added_at || '').trim(),
      })).filter((benefit) => benefit.heading || benefit.description || benefit.source || benefit.added_at)
    },
    listToCsv(value) {
      return (Array.isArray(value) ? value : []).join(', ')
    },
    csvToList(value) {
      return String(value || '').split(',').map((item) => item.trim()).filter(Boolean)
    },
    isValidUrl(value) {
      if (!String(value || '').trim()) return true
      try {
        new URL(String(value).trim())
        return true
      } catch {
        return false
      }
    },
    isValidEmail(value) {
      const email = String(value || '').trim()
      return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    validateForm() {
      const errors = []
      if (!String(this.form.name || '').trim()) errors.push('Company name is required.')
      if (!String(this.form.slug || '').trim()) errors.push('Slug is required.')
      if (!this.isValidEmail(this.form.contact_email)) errors.push('Contact email must be a valid email address.')
      ;['website', 'logo'].forEach((field) => {
        if (!this.isValidUrl(this.form[field])) errors.push(`${this.fieldLabel(field)} must be a valid URL.`)
      })
      this.form.job_links.forEach((link, index) => {
        if (link.url && !this.isValidUrl(link.url)) errors.push(`Job link ${index + 1} URL must be valid.`)
        if ((link.title || link.description) && !link.url) errors.push(`Job link ${index + 1} needs a URL.`)
      })
      this.form.social_links.forEach((link, index) => {
        if (link.url && !this.isValidUrl(link.url)) errors.push(`Social link ${index + 1} URL must be valid.`)
      })
      this.form.recent_key_hires.forEach((hire, index) => {
        if (hire.url && !this.isValidUrl(hire.url)) errors.push(`Recent hire ${index + 1} source URL must be valid.`)
      })
      this.form.recent_capital_raisings.forEach((raising, index) => {
        if (raising.url && !this.isValidUrl(raising.url)) errors.push(`Capital raising ${index + 1} source URL must be valid.`)
      })
      this.form.benefits.forEach((benefit, index) => {
        if (benefit.source && !this.isValidUrl(benefit.source)) errors.push(`Benefit ${index + 1} source URL must be valid.`)
      })
      this.form.key_projects.forEach((project, index) => {
        const hasProjectData = project.name || project.stage || project.description || project.location_label || this.cleanStringList(project.commodities).length
        if (hasProjectData && !project.name && !project.stage) errors.push(`Project ${index + 1} needs at least a name or stage.`)
      })
      return errors
    },
    addAlias() {
      const alias = this.aliasInput.trim()
      const key = this.companyNameKey(alias)
      if (!alias || key.length < 2) return
      const existing = new Set((this.form.company_aliases || []).map((item) => this.companyNameKey(item)))
      if (!existing.has(key)) this.form.company_aliases.push(alias)
      this.aliasInput = ''
    },
    removeAlias(alias) {
      this.form.company_aliases = this.form.company_aliases.filter((item) => item !== alias)
    },
    findLocalConflicts(names) {
      const selectedId = String(this.form.id)
      const keys = new Map()
      names.forEach((name) => {
        const key = this.companyNameKey(name)
        if (key) keys.set(key, name)
      })
      const conflicts = []
      this.companies.forEach((company) => {
        if (String(company.id) === selectedId) return
        const candidates = [company.name, ...(company.company_aliases || [])]
        candidates.forEach((candidate) => {
          const key = this.companyNameKey(candidate)
          if (key && keys.has(key)) {
            conflicts.push({
              value: keys.get(key),
              normalized_name: key,
              company_id: company.id,
              company_name: company.name,
              source: candidate === company.name ? 'company_name' : 'company_alias',
              matched_alias: candidate === company.name ? null : candidate,
            })
          }
        })
      })
      return conflicts
    },
    companyNameKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/[&+]/g, ' and ')
        .replace(/\([^)]*\)/g, ' ')
        .replace(/\b(pty\s*ltd|pty|proprietary|p\s*\/?\s*l|p\s*l|ltd|limited|nl|plc|inc|incorporated|corp|corporation|company|co|llc|llp|lp|gmbh|ag|sa|sarl|bv|nv)\b\.?/gi, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    normalizeSearch(value) {
      return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim()
    },
    clearFilters() {
      this.query = ''
      this.industryFilter = ''
      this.activityFilter = 'all'
    },
    companyInitial(name) {
      return String(name || '?').trim().charAt(0).toUpperCase() || '?'
    },
    fieldLabel(field) {
      return field.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    },
    openCreditModal() {
      this.showCreditModal = true
      this.creditAmount = 1
      this.creditReason = ''
      this.confirmCreditAddition = false
    },
    closeCreditModal() {
      this.showCreditModal = false
      this.creditAmount = 1
      this.creditReason = ''
      this.confirmCreditAddition = false
    },
    confirmAddCredits() {
      if (!this.isValidCreditForm || !this.selectedCompany) return
      this.toggleLoading(true)
      adminService.addCompanyCredits({
        companyId: this.selectedCompany.id,
        credits: this.creditAmount,
        reason: this.creditReason || 'Admin allocation',
      })
        .then(() => {
          const index = this.companies.findIndex((company) => String(company.id) === String(this.selectedCompany.id))
          if (index !== -1) {
            this.companies[index].credits = Number(this.companies[index].credits || 0) + Number(this.creditAmount)
          }
          this.$root.notify(`Added ${this.creditAmount} credits to ${this.selectedCompany.name}`, 'success')
          this.closeCreditModal()
        })
        .catch(() => {
          this.$root.notify('Failed to add credits', 'error')
        })
        .finally(() => {
          this.toggleLoading(false)
        })
    },
  },
  created() {
    this.fetchCompanies()
  },
}
</script>

<style scoped>
.field {
  @apply h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100;
}

textarea.field {
  @apply h-auto py-3;
}

.label {
  @apply mb-2 block text-sm font-medium text-slate-700;
}

.icon-button {
  @apply inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50;
}

.primary-button {
  @apply inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50;
}

.small-button {
  @apply inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50;
}

.icon-only-button {
  @apply inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-950;
}

.danger-button {
  @apply inline-flex items-center justify-center rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50;
}
</style>
